import { parseUtcInstant } from "./dateTimeUtils";

export function sortRowsByTimeDesc(rows, timeAccessor) {
  return [...rows].sort((a, b) => {
    const ta = parseUtcInstant(timeAccessor(a))?.valueOf() ?? 0;
    const tb = parseUtcInstant(timeAccessor(b))?.valueOf() ?? 0;
    return tb - ta;
  });
}

export function sortRowsByTimeAsc(rows, timeAccessor) {
  return [...rows].sort((a, b) => {
    const ta = parseUtcInstant(timeAccessor(a))?.valueOf() ?? 0;
    const tb = parseUtcInstant(timeAccessor(b))?.valueOf() ?? 0;
    return ta - tb;
  });
}

function uniqueRowsByKey(rows, dedupeKey) {
  const seen = new Set();
  return rows.filter((row) => {
    const key = dedupeKey(row);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function mergeLiveTelemetryRows({
  historyRows,
  liveRows,
  limit,
  dedupeKey,
  matchesDateFilter,
  dateRange,
  timeAccessor,
}) {
  // Keep every API page row. Minute-level live keys must not collapse
  // multiple readings from the same minute on page 1.
  const filteredHistory = dateRange
    ? historyRows.filter((row) => matchesDateFilter(row, dateRange))
    : historyRows;

  if (!liveRows.length) {
    return limit ? filteredHistory.slice(0, limit) : filteredHistory;
  }

  const historyKeys = new Set(
    filteredHistory.map(dedupeKey).filter(Boolean),
  );
  const prepend = uniqueRowsByKey(
    liveRows.filter(
      (row) =>
        !historyKeys.has(dedupeKey(row)) && matchesDateFilter(row, dateRange),
    ),
    dedupeKey,
  );

  if (!prepend.length) {
    return limit ? filteredHistory.slice(0, limit) : filteredHistory;
  }

  const merged = sortRowsByTimeDesc(
    [...prepend, ...filteredHistory],
    timeAccessor,
  );
  return limit ? merged.slice(0, limit) : merged;
}

/** Append socket readings to chart history in chronological order (no API refetch). */
export function mergeLiveChartTelemetryRows({
  historyRows,
  liveRows,
  dedupeKey,
  matchesDateFilter,
  dateRange,
  timeAccessor,
}) {
  const filteredHistory = dateRange
    ? historyRows.filter((row) => matchesDateFilter(row, dateRange))
    : historyRows;

  if (!liveRows.length) return filteredHistory;

  const historyKeys = new Set(
    filteredHistory.map(dedupeKey).filter(Boolean),
  );
  const append = uniqueRowsByKey(
    liveRows.filter(
      (row) =>
        !historyKeys.has(dedupeKey(row)) && matchesDateFilter(row, dateRange),
    ),
    dedupeKey,
  );

  if (!append.length) return filteredHistory;

  return sortRowsByTimeAsc([...filteredHistory, ...append], timeAccessor);
}

export function countLiveOnlyRows({
  historyRows,
  liveRows,
  dedupeKey,
  matchesDateFilter,
  dateRange,
  timeAccessor,
}) {
  const historyKeys = new Set(historyRows.map(dedupeKey));
  return liveRows.filter(
    (row) =>
      !historyKeys.has(dedupeKey(row)) && matchesDateFilter(row, dateRange),
  ).length;
}
